import { useTranslate } from '@tolgee/react';
import { Button, Form, message, Radio, Space } from 'antd';
import type { FormInstance, ButtonProps } from 'antd';
import { useState } from 'react';
import { JustRightHeightModal } from 'components/styled/scoped';
import {
  refuseReason,
  refuse as refuseApi,
  RefuseReasonResponse,
  RefuseBody,
} from 'services/api/return';
import { handleBuyerCancellation } from 'services/api/refund';
import { useRequest } from 'ahooks';
import { RefuseUploadImage, useRefuseUploadImageValidate } from './RefuseUploadImage';
import { UploadFile } from 'antd/es/upload/interface';
import { getApiErrorCodeMsg } from 'services/api/utils/errorCode';
import { DescriptionField } from './DisputeModal';
import { FakeRefuseButton } from './FakeRefuse';

type ImplementProps = {
  region?: General.UpperCaseRegion;
  internalStoreId: string;
  form: FormInstance;
  reasons: RefuseReasonResponse['availableReasonList'];
};

function Implement(props: ImplementProps) {
  const { t } = useTranslate();

  const { region, form, reasons, internalStoreId } = props;

  const refuseUploadImageValidate = useRefuseUploadImageValidate(true);

  return (
    <div>
      <Form layout="vertical" form={form}>
        <Form.Item required label={t('ViewRefund.tiktok.chooseReason')} name="reasonId">
          <Radio.Group>
            <Space direction="vertical">
              {reasons.map((item) => (
                <Radio key={item.reasonId} value={item.reasonId}>
                  {t({
                    key: `tiktokRefuseReason.${item.text}`,
                    defaultValue: item.text,
                  })}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="images"
          label={`${t('trade.上传证据')}(${t('ViewRefund.tiktok.partial')})`}
          rules={[
            {
              validator: (_, value) => {
                const errorMsg = refuseUploadImageValidate(value);
                return errorMsg ? Promise.reject(errorMsg) : Promise.resolve();
              },
              validateTrigger: ['onSubmit'],
            },
          ]}
        >
          <RefuseUploadImage
            internalStoreId={internalStoreId}
            reqSource="REFUND_TIKTOK"
            accepts={['jpg', 'jpeg', 'png']}
            countLimit={6}
            sizeLimit={5}
          />
        </Form.Item>

        <Form.Item
          name="comment"
          label={`${t('ViewRefund.tiktok.addition.comment')}(${t('ViewRefund.tiktok.partial')})`}
        >
          <DescriptionField
            region={region}
            form={form}
            placeholder={t('trade.请输入')}
            fieldName="comment"
          />
        </Form.Item>
      </Form>
    </div>
  );
}

type TikTokRefuseProps = {
  platform: 'TIKTOK' | 'TIKTOK_US';
  region?: General.UpperCaseRegion;
  returnSn?: string;
  externalStoreId: string;
  internalStoreId: string;
  externalTradeId?: string;
  btnProps?: ButtonProps;
  onRefused?: () => void;
  // 退货退款 和 取消订单 状态的 确认和取消逻辑调用的是不同的接口
  inCanceled?: boolean;
  operate: RefuseBody['operate'];
  dueDateMs?: string;
};

function TikTokRefuse(props: TikTokRefuseProps) {
  const {
    platform,
    region,
    btnProps,
    externalStoreId,
    returnSn,
    onRefused,
    externalTradeId,
    internalStoreId,
    inCanceled = false,
    operate,
    dueDateMs,
  } = props;
  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const { loading, runAsync } = useRequest((params) => refuseReason(params), {
    manual: true,
  });
  const { loading: mutateLoading, runAsync: refuse } = useRequest(
    (data) =>
      refuseApi({
        ...data,
        operate,
      }),
    {
      manual: true,
    },
  );
  const { loading: mutateLoading2, runAsync: runAsync2 } = useRequest(
    (data) => handleBuyerCancellation(data),
    {
      manual: true,
    },
  );
  const [reasons, setReasons] = useState<RefuseReasonResponse['availableReasonList']>([]);

  const open = async () => {
    try {
      const data = await runAsync({
        externalType: platform,
        externalStoreId,
        ...(returnSn ? { returnSn } : { externalTradeId }),
      });

      const { availableReasonList } = data;
      setReasons(availableReasonList || []);
      setVisible(true);
    } catch (e) {
      message.error(t('ViewRefund.获取拒绝原因列表失败，请稍后再试'));
    }
  };

  const [refuseFailed, setRefuseFailed] = useState(false);

  const submit = () => {
    form.validateFields().then(async (formValues) => {
      const { images, ...values } = formValues;
      if (values.reasonId === undefined) {
        return message.error(t('ViewRefund.tiktok.chooseReason.please'));
      }
      const image = images?.map((item: UploadFile<any>) => {
        return item.response?.data?.platformUrl || item.response?.data?.url;
      });

      try {
        if (inCanceled) {
          await runAsync2({
            externalStoreId,
            orderId: externalTradeId,
            platform: platform,
            isAccept: false,
            image,
            ...values,
          });
        } else {
          await refuse({
            externalStoreId,
            returnSn,
            image,
            ...values,
          });
        }
        message.success(t('ViewRefund.tiktok.refuse.already'));
        if (onRefused) {
          onRefused();
        }
        onCancel();
      } catch (e: any) {
        if (e.response.data.code === 1103) {
          message.error(t('ViewRefund.tiktok.order.status.change'));
        } else if (e.response.data.code === 5020) {
          setRefuseFailed(true);
          onCancel();
          message.error(
            t(
              dueDateMs && dueDateMs !== '0' && Number(dueDateMs) < Date.now()
                ? '当前退单已超过处理截止时间，无法进行拒绝操作'
                : '根据Tiktok政策，该退单申请不支持拒绝，如需拒绝请前往卖家中心联系平台客服',
            ),
          );
        } else {
          message.error(getApiErrorCodeMsg(e.response, 'ViewRefund.tiktok.retry'));
        }
      }
    });
  };
  const onCancel = () => {
    form.resetFields();
    setVisible(false);
  };

  const modalInstance = visible && (
    <JustRightHeightModal
      visible={visible}
      width={600}
      onCancel={onCancel}
      title={t('ViewRefund.tiktok.refuseRefundApplication')}
      okButtonProps={{
        loading: mutateLoading || mutateLoading2,
      }}
      onOk={submit}
    >
      <Implement region={region} internalStoreId={internalStoreId} form={form} reasons={reasons} />
    </JustRightHeightModal>
  );
  return (
    <>
      {modalInstance}
      {refuseFailed ? (
        <FakeRefuseButton
          title={t(
            dueDateMs && dueDateMs !== '0' && Number(dueDateMs) < Date.now()
              ? '当前退单已超过处理截止时间，无法进行拒绝操作'
              : '根据Tiktok政策，该退单申请不支持拒绝，如需拒绝请前往卖家中心联系平台客服',
          )}
        />
      ) : (
        <Button size="small" {...btnProps} loading={loading} onClick={() => open()}>
          {t('tableColumn.拒绝')}
        </Button>
      )}
    </>
  );
}

export default TikTokRefuse;
